import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { getAgentMe } from "../api/agent";

/**
 * Chrome for the agent-only experience: `Layout.tsx` minus the sidebar and
 * minus the `getMyPermissions` query.
 *
 * An agent token 403s `GET /api/users/me/permissions` (it is not an operator
 * endpoint an agent principal may reach — `get_current_user`'s fail-closed
 * gate), so `Layout` cannot be reused as-is: its sidebar is built entirely
 * from that permission set. There is also nothing to navigate to — an agent
 * session can reach exactly one route (`/agent`) — so a sidebar would be
 * either empty or, worse, full of links that 403 the moment they're clicked.
 *
 * Takes `children` directly rather than rendering an `<Outlet/>`: this is not
 * a nested layout route (there is only ever one child, `AgentPanelPage`, and
 * it is composed directly by App.tsx's `AgentPanelRoute` — see that file's
 * comment for why `/agent` can't be two separate `<Route>` entries).
 */
export function AgentLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  // Same queryKey as AgentPanelPage's own seat lookup — React Query dedupes
  // the two into one request since both mount under the same provider.
  const { data: me } = useQuery({ queryKey: ["agent-me"], queryFn: getAgentMe });

  async function handleSignOut() {
    await signOut();
    navigate("/agent-login", { replace: true });
  }

  const name = me?.display_name || user?.display_name || me?.username || "Agent";
  const seat = me?.extension_name ?? (user?.extension_id != null ? String(user.extension_id) : null);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Slate header ties the shell to AgentConsole's own on-call tone — a
          deliberate visual signal that this is the agent console, not the
          operator app, before a single word is read. */}
      <header className="h-14 shrink-0 bg-slate-900 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-10">
        <span className="font-bold text-white text-lg tracking-tight">Reliadata</span>
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/70" role="status">
            {name}
            {seat && (
              <span className="ml-2 text-[10px] uppercase bg-white/10 text-white rounded px-1.5 py-0.5">
                Ext {seat}
              </span>
            )}
          </span>
          <button
            onClick={() => void handleSignOut()}
            className="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-sm text-white transition"
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="max-w-2xl mx-auto w-full">{children}</div>
      </main>
    </div>
  );
}
